import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as htmlToImage from 'html-to-image';
import { buildHtmlDocument } from './htmlBlock';

const DEFAULT_MIN_HEIGHT = 120;

export const getHtmlDocumentHeight = (
  documentElement,
  body,
  minHeight = DEFAULT_MIN_HEIGHT
) => {
  const candidates = [
    documentElement?.scrollHeight,
    documentElement?.offsetHeight,
    documentElement?.clientHeight,
    body?.scrollHeight,
    body?.offsetHeight,
    body?.clientHeight,
    body?.getBoundingClientRect?.().height,
  ].filter((value) => Number.isFinite(value));

  return Math.max(minHeight, ...candidates.map((value) => Math.ceil(value)));
};

const HtmlBlockRenderer = ({
  html = '',
  className = '',
  captureAsImage = false,
  interactive = true,
  minHeight = DEFAULT_MIN_HEIGHT,
  title = 'Contenido del bloque HTML',
}) => {
  const iframeRef = useRef(null);
  const cleanupRef = useRef(() => {});
  const [height, setHeight] = useState(minHeight);
  const [snapshot, setSnapshot] = useState('');
  const [captureError, setCaptureError] = useState(false);
  const sourceDocument = useMemo(() => buildHtmlDocument(html), [html]);

  useEffect(() => {
    setHeight(minHeight);
    setSnapshot('');
    setCaptureError(false);
  }, [captureAsImage, minHeight, sourceDocument]);

  useEffect(() => () => cleanupRef.current(), []);

  const handleLoad = () => {
    cleanupRef.current();

    const frame = iframeRef.current;
    const frameDocument = frame?.contentDocument;
    if (!frameDocument) return;

    const updateHeight = () => {
      setHeight(
        getHtmlDocumentHeight(
          frameDocument.documentElement,
          frameDocument.body,
          minHeight
        )
      );
    };
    const preventNavigation = (event) => {
      if (event.target?.closest?.('a[href]')) {
        event.preventDefault();
      }
    };
    const preventSubmit = (event) => event.preventDefault();

    updateHeight();
    frameDocument.addEventListener('load', updateHeight, true);
    frameDocument.addEventListener('click', preventNavigation, true);
    frameDocument.addEventListener('submit', preventSubmit, true);

    const ResizeObserverClass =
      frame.contentWindow?.ResizeObserver || window.ResizeObserver;
    const resizeObserver = ResizeObserverClass
      ? new ResizeObserverClass(updateHeight)
      : null;

    if (resizeObserver) {
      if (frameDocument.documentElement) {
        resizeObserver.observe(frameDocument.documentElement);
      }
      if (frameDocument.body) {
        resizeObserver.observe(frameDocument.body);
      }
    }

    let captureTimer;
    if (captureAsImage) {
      captureTimer = frame.contentWindow.setTimeout(async () => {
        try {
          await frameDocument.fonts?.ready;
          updateHeight();
          const width = Math.max(
            frame.clientWidth,
            frameDocument.documentElement?.scrollWidth || 0,
            frameDocument.body?.scrollWidth || 0
          );
          const captureHeight = getHtmlDocumentHeight(
            frameDocument.documentElement,
            frameDocument.body,
            minHeight
          );
          const dataUrl = await htmlToImage.toPng(
            frameDocument.documentElement,
            {
              backgroundColor: '#ffffff',
              pixelRatio: 2,
              width,
              height: captureHeight,
            }
          );
          cleanupRef.current();
          cleanupRef.current = () => {};
          setSnapshot(dataUrl);
        } catch (error) {
          console.error('No fue posible preparar el bloque HTML para PDF:', error);
          setCaptureError(true);
        }
      }, 150);
    }

    cleanupRef.current = () => {
      if (captureTimer) {
        frame.contentWindow.clearTimeout(captureTimer);
      }
      resizeObserver?.disconnect();
      frameDocument.removeEventListener('load', updateHeight, true);
      frameDocument.removeEventListener('click', preventNavigation, true);
      frameDocument.removeEventListener('submit', preventSubmit, true);
    };
  };

  const frame = (
    <iframe
        ref={iframeRef}
        data-html-block-renderer="true"
        title={title}
        sandbox="allow-same-origin"
        srcDoc={sourceDocument}
        onLoad={handleLoad}
        scrolling="no"
        className={`block w-full border-0 bg-white ${captureAsImage ? '' : className}`}
        style={{
          display: snapshot ? 'none' : 'block',
          height: `${height}px`,
          pointerEvents: interactive ? 'auto' : 'none',
        }}
      />
  );

  if (!captureAsImage) return frame;

  return (
    <div
      className={`w-full bg-white ${className}`}
      data-html-capture-state={snapshot ? 'ready' : captureError ? 'error' : 'loading'}
    >
      {snapshot && <img src={snapshot} alt={title} className="block h-auto w-full" />}
      {captureError && (
        <div className="flex min-h-[120px] items-center justify-center border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500">
          No fue posible representar este bloque HTML en el PDF.
        </div>
      )}
      {!captureError && frame}
    </div>
  );
};

export default HtmlBlockRenderer;
